import { ChangeDetectionStrategy, Component, ElementRef, HostListener } from "@angular/core";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { IShellPopover } from "@common/shell/shell-popover-link-item/shell-popover-link-item";
import { UserService } from "@common/user/user.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { BehaviorSubject, debounceTime, filter, Subject, switchMap, tap } from "rxjs";
import { ActivityFeedService } from "../activity-feed.service";
import { ActivityInstance } from "../activity-feed-manager/activity-instance";
import { ActivityFeedSignalRHub } from "../activity-feed-signalr-hub";
import { PersonalActivityFeedPageRoute } from "../personal-activity-feed-page/personal-activity-feed-page.component";

const NotificationCountLimit = 10;

@Component({
    selector: "adapt-activity-notification-content",
    templateUrl: "./activity-notification-content.component.html",
    styleUrl: "./activity-notification-content.component.css",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ActivityNotificationContentComponent extends BaseComponent implements IShellPopover {
    public isVisible$ = new Subject<boolean>();
    public activityInstances: ActivityInstance[] = [];
    public lastReadTimestamp = new Date(0);

    private latestTimestamp?: Date;
    private triggerUpdate$ = new BehaviorSubject<void>(undefined);
    private currentPerson?: Person;

    public constructor(
        private activityFeedService: ActivityFeedService,
        private userService: UserService,
        activityFeedHub: ActivityFeedSignalRHub,
        elementRef: ElementRef,
    ) {
        super(elementRef);

        this.triggerUpdate$.pipe(
            debounceTime(100),
            switchMap(() => this.userService.currentPerson$),
            filter((person) => !!person),
            tap((person) => this.currentPerson = person),
            switchMap((person) => this.activityFeedService.getPersonalActivityTimestamp(person)),
            tap((timestamp) => this.lastReadTimestamp = timestamp ?? new Date(0)),
            switchMap(() => this.fetchInitialActivity(this.currentPerson!)),
            switchMap(() => activityFeedHub.onPersonalActivity(this.currentPerson!)),
            switchMap(() => this.fetchNewerActivity(this.currentPerson!)),
            this.takeUntilDestroyed(),
        ).subscribe();

        // don't trigger update from personDetail change as that will mark all as read
        // - onPersonalActivity above will update the content but won't reset the read timestamp until this is closed

        // in order to sync with notification component (the guy with bell), lastReadTimestamp can also be updated from this observable
        // - e.g. other session read, bell's badge will be gone - so this content will need to be updated too
        this.activityFeedService.activityFeedTimestampUpdated$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((timestamp) => this.lastReadTimestamp = timestamp);
    }

    @HostListener("click")
    public onClick() {
        this.isVisible$.next(false);
        this.triggerUpdate$.next();
    }

    public viewAllActivities() {
        PersonalActivityFeedPageRoute.gotoRoute().subscribe();
    }

    public emitVisibleChange(isVisible: boolean) {
        if (!isVisible) {
            if (this.latestTimestamp && this.latestTimestamp > this.lastReadTimestamp) {
                // closing popover with updated entry -> update last read timestamp
                this.activityFeedService.updatePersonalActivityTimestamp(this.currentPerson!).pipe(
                    this.takeUntilDestroyed(),
                ).subscribe();
            }

            this.triggerUpdate$.next();
        }
    }

    private fetchInitialActivity(person: Person) {
        return this.activityFeedService.getInitialPersonalActivity(person).pipe(
            tap((activityInstances) => this.updateActivityInstances(activityInstances)),
        );
    }

    private fetchNewerActivity(person: Person) {
        if (!this.latestTimestamp) {
            return this.fetchInitialActivity(person);
        } else {
            return this.activityFeedService.getNewerPersonalActivity(person, this.latestTimestamp).pipe(
                tap((activityInstances) => {
                    activityInstances = activityInstances.concat(this.activityInstances);
                    this.updateActivityInstances(activityInstances);
                }),
            );
        }
    }

    private updateActivityInstances(activityInstances: ActivityInstance[]) {
        // make sure latest timestamp first
        activityInstances.sort((a, b) => b.dateTime.getTime() - a.dateTime.getTime());
        activityInstances = activityInstances.filter((activity) => activity.recentlyTriggered);
        if (activityInstances.length > 0) {
            this.latestTimestamp = activityInstances[0].dateTime;
        }

        if (activityInstances.length > NotificationCountLimit) {
            activityInstances = activityInstances.slice(0, NotificationCountLimit);
        }

        this.activityInstances = activityInstances;
        this.latestTimestamp = (activityInstances.length > 0) ? activityInstances[0].dateTime : undefined;
    }
}
